import type { RealMachinesDatabase } from '@/db/database'
import { getDatabase } from '@/db/database'
import { DatabaseOperationError, executeDatabaseOperation } from '@/db/errors'
import { createSignatureInputSchema, signatureSchema } from '@/db/schemas'
import { createUuid } from '@/db/uuid'
import type { CreateSignatureInput, Signature } from '@/db/types'
import { requireRecord, writeAuditLog } from './helpers'

export class SignatureRepository {
  constructor(private readonly database: RealMachinesDatabase = getDatabase()) {}

  async create(input: CreateSignatureInput): Promise<Signature> {
    return executeDatabaseOperation('salvar assinatura', async () => {
      const data = createSignatureInputSchema.parse(input)
      if (data.inspectionId) {
        return this.saveForInspection(data.inspectionId, data.type, data.blob, data.mimeType)
      }
      return this.database.transaction(
        'rw',
        [
          this.database.signatures,
          this.database.inspections,
          this.database.technicians,
          this.database.auditLogs,
        ],
        async () => {
          if (data.technicianId) {
            requireRecord(await this.database.technicians.get(data.technicianId), 'Técnico')
          }
          const now = new Date().toISOString()
          const signature = signatureSchema.parse({ ...data, id: createUuid(), createdAt: now })
          await this.database.signatures.add(signature)
          if (signature.inspectionId) {
            await this.database.inspections.update(signature.inspectionId, {
              ...(signature.type === 'client'
                ? { clientSignatureId: signature.id }
                : { technicianSignatureId: signature.id }),
              updatedAt: now,
            })
          } else if (signature.technicianId) {
            await this.database.technicians.update(signature.technicianId, {
              signatureId: signature.id,
              updatedAt: now,
            })
          }
          await writeAuditLog(this.database, 'signature', signature.id, 'create', {}, now)
          return signature
        },
      )
    })
  }

  async saveForInspection(
    inspectionId: string,
    type: Signature['type'],
    blob: Blob,
    mimeType: string,
  ): Promise<Signature> {
    return executeDatabaseOperation('confirmar assinatura da inspeção', async () => {
      const data = createSignatureInputSchema.parse({ inspectionId, type, blob, mimeType })
      return this.database.transaction(
        'rw',
        [this.database.signatures, this.database.inspections, this.database.auditLogs],
        async () => {
          const inspection = requireRecord(
            await this.database.inspections.get(inspectionId),
            'Inspeção',
          )
          if (inspection.status !== 'draft') {
            throw new DatabaseOperationError(
              'protected_record',
              'Assinaturas só podem ser alteradas enquanto o relatório está em rascunho.',
              'Reabra o relatório para revisão antes de alterar uma assinatura.',
            )
          }
          const field = type === 'client' ? 'clientSignatureId' : 'technicianSignatureId'
          const existingId = inspection[field]
          const existing = existingId ? await this.database.signatures.get(existingId) : undefined
          const now = new Date().toISOString()
          const signature = signatureSchema.parse({
            ...data,
            id:
              existing?.inspectionId === inspectionId && existing.type === type
                ? existing.id
                : createUuid(),
            createdAt: existing?.createdAt ?? now,
          })
          await this.database.signatures.put(signature)
          await this.database.inspections.update(inspectionId, {
            [field]: signature.id,
            ...(type === 'client'
              ? {
                  clientSignatureWaiverReason: '',
                  clientSignatureWaiverConfirmedAt: undefined,
                }
              : {}),
            updatedAt: now,
          })
          await writeAuditLog(
            this.database,
            'signature',
            signature.id,
            existing ? 'update' : 'create',
            { inspectionId, type },
            now,
          )
          return signature
        },
      )
    })
  }

  async getById(id: string): Promise<Signature | undefined> {
    return executeDatabaseOperation('consultar assinatura', async () => {
      const signature = await this.database.signatures.get(id)
      return signature ? signatureSchema.parse(signature) : undefined
    })
  }

  async updateBlob(id: string, blob: Blob, mimeType: string): Promise<Signature> {
    return executeDatabaseOperation('editar assinatura', async () =>
      this.database.transaction(
        'rw',
        [this.database.signatures, this.database.inspections, this.database.auditLogs],
        async () => {
          const existing = signatureSchema.parse(
            requireRecord(await this.database.signatures.get(id), 'Assinatura'),
          )
          if (existing.inspectionId) {
            const inspection = requireRecord(
              await this.database.inspections.get(existing.inspectionId),
              'Inspeção',
            )
            if (inspection.status !== 'draft') {
              throw new DatabaseOperationError(
                'protected_record',
                'Assinatura de relatório fechado não pode ser alterada.',
              )
            }
          }
          const signature = signatureSchema.parse({ ...existing, blob, mimeType })
          await this.database.signatures.put(signature)
          await writeAuditLog(
            this.database,
            'signature',
            id,
            'update',
            { fields: ['blob', 'mimeType'] },
            new Date().toISOString(),
          )
          return signature
        },
      ),
    )
  }

  async delete(id: string): Promise<void> {
    return executeDatabaseOperation('apagar assinatura', async () =>
      this.database.transaction(
        'rw',
        [
          this.database.signatures,
          this.database.inspections,
          this.database.technicians,
          this.database.auditLogs,
        ],
        async () => {
          const signature = signatureSchema.parse(
            requireRecord(await this.database.signatures.get(id), 'Assinatura'),
          )
          const now = new Date().toISOString()
          if (signature.inspectionId) {
            const inspection = await this.database.inspections.get(signature.inspectionId)
            if (inspection && inspection.status !== 'draft') {
              throw new DatabaseOperationError(
                'protected_record',
                'Assinatura de inspeção concluída não pode ser apagada.',
              )
            }
            if (inspection) {
              await this.database.inspections.update(inspection.id, {
                ...(inspection.clientSignatureId === id ? { clientSignatureId: undefined } : {}),
                ...(inspection.technicianSignatureId === id
                  ? { technicianSignatureId: undefined }
                  : {}),
                updatedAt: now,
              })
            }
          }
          if (signature.technicianId) {
            const technician = await this.database.technicians.get(signature.technicianId)
            if (technician?.signatureId === id) {
              await this.database.technicians.update(technician.id, {
                signatureId: undefined,
                updatedAt: now,
              })
            }
          }
          await this.database.signatures.delete(id)
          await writeAuditLog(this.database, 'signature', id, 'delete', {}, now)
        },
      ),
    )
  }
}
